import type { ComponentType } from 'react';
import { SectionHeader } from '../ui/SectionHeader';
import { SkeletonLoader } from '../SkeletonLoader';
import { LogbookActor } from './LogbookActor';
import { LogbookServerChip } from './LogbookServerChip';
import { LogbookObject } from './LogbookObject';
import { cn } from '../../utils/cn';
import { actionMeta, dimensionLabel, formatCount, outcomeMeta } from '../../utils/logbook';
import type { LogbookAggregationResult, LogbookGroupBy } from '../../types/logbook';
import type { LogbookServerView } from '../../hooks/useLogbookServers';

interface LogbookBreakdownProps {
  title: string;
  icon?: ComponentType<{ className?: string }>;
  aggregation: LogbookAggregationResult | null;
  loading?: boolean;
  serversById?: Map<string, LogbookServerView>;
  /** Clicking a row adds it to the filters. */
  onSelect?: (groupBy: LogbookGroupBy, value: string) => void;
  /** Rows to show before the list is cut. */
  limit?: number;
  /** Render without the card surface and without its own header, for use inside another card. */
  bare?: boolean;
  className?: string;
}

const TONE_BY_GROUP: Record<LogbookGroupBy, string> = {
  action: 'var(--accent-300)',
  actor_id: 'var(--tone-purple-text)',
  server_id: 'var(--tone-info-text)',
  namespace: 'var(--tone-info-text)',
  object_id: 'var(--tone-amber-text)',
  outcome: 'var(--tone-orange-text)',
  dimension: 'var(--tone-success-text)',
  time: 'var(--accent-300)',
};

/**
 * The shape of the matching evidence: which actions, which actors, which servers.
 *
 * Each count covers every matching event, not the page on screen, so this is the honest answer to
 * "who did the most of this" even when the results run to thousands. Rows are clickable, so reading
 * the shape and then narrowing to one row is one gesture rather than a retyped filter.
 */
export const LogbookBreakdown = ({
  title, icon, aggregation, loading = false, serversById, onSelect, limit = 8, bare = false, className,
}: LogbookBreakdownProps) => {
  const groupBy = aggregation?.group_by ?? 'action';
  const groups = (aggregation?.groups ?? []).slice(0, limit);
  const max = groups.reduce((highest, group) => Math.max(highest, group.event_count), 0);
  const colour = TONE_BY_GROUP[groupBy] ?? 'var(--accent-300)';

  const renderValue = (value: string) => {
    switch (groupBy) {
      case 'action':
        return <span className="truncate text-white">{actionMeta(value).label}</span>;
      case 'actor_id':
        return <LogbookActor actorId={value} actorName="" actorKind="player" />;
      case 'server_id':
        return <LogbookServerChip serverId={value} server={serversById?.get(value)} />;
      case 'object_id':
        return <LogbookObject objectId={value} />;
      case 'outcome':
        return <span className="truncate text-white">{outcomeMeta(value).label}</span>;
      case 'dimension':
        return <span className="truncate text-white">{dimensionLabel(value)}</span>;
      default:
        return <span className="truncate text-white font-mono text-xs">{value}</span>;
    }
  };

  return (
    <div className={cn(!bare && 'glass-card p-4 md:p-5', className)}>
      {!bare && (
        <SectionHeader
          title={title}
          icon={icon}
          count={aggregation ? aggregation.groups.length : undefined}
          description={aggregation?.state === 'partial' ? 'Largest groups only.' : undefined}
        />
      )}

      {loading && groups.length === 0 ? (
        <div className="space-y-2.5">
          {Array.from({ length: 5 }, (_, index) => <SkeletonLoader key={index} className="h-7 w-full" />)}
        </div>
      ) : groups.length === 0 ? (
        <p className="text-sm text-glass-500">Nothing matched in this window.</p>
      ) : (
        <ul className="space-y-1">
          {groups.map((group) => {
            const value = String(group.value);
            const share = max > 0 ? Math.max(2, Math.round((group.event_count / max) * 100)) : 0;
            const row = (
              <>
                {/* The bar sits behind the label so the label stays at full contrast. */}
                <span
                  className="absolute inset-y-0 left-0 rounded-md opacity-[0.16] pointer-events-none"
                  style={{ width: `${share}%`, backgroundColor: colour }}
                  aria-hidden
                />
                <span className="relative flex-1 min-w-0 flex items-center gap-2 text-sm">{renderValue(value)}</span>
                <span className="relative text-xs font-medium tabular-nums text-glass-300 shrink-0">
                  {formatCount(group.event_count)}
                </span>
              </>
            );

            return (
              <li key={value}>
                {onSelect ? (
                  <button
                    type="button"
                    onClick={() => onSelect(groupBy, value)}
                    className="relative w-full flex items-center gap-3 px-2 py-1.5 rounded-md text-left hover:bg-glass-200/[0.06] transition-colors"
                    title={`Filter by ${value}`}
                  >
                    {row}
                  </button>
                ) : (
                  <div className="relative w-full flex items-center gap-3 px-2 py-1.5 rounded-md">{row}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
